import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import supabaseClient from '@/api/supabaseClient';
import { Database } from '@/_constants/database.types';

type GradingPromptRow = Database['public']['Tables']['grading_prompts']['Row'];

export interface GradingPrompt extends GradingPromptRow {
  // Extended with joined data from question_categories
  category_name?: string;
  subject?: string;
  paper_number?: string;
}

export interface GradingPromptWithCategory {
  id: string;
  category_code: string;
  prompt_template: string;
  description: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  question_categories: {
    name: string;
    subject: string;
    paper_number: string;
    category: string;
  };
}

export interface UpdateGradingPromptParams {
  id: string;
  prompt_template?: string;
  description?: string | null;
  is_active?: boolean;
}

const fetchGradingPrompts = async (): Promise<GradingPromptWithCategory[]> => {
  const { data, error } = await supabaseClient
    .from('grading_prompts')
    .select(`
      *,
      question_categories!grading_prompts_category_code_fkey (
        name,
        subject,
        paper_number,
        category
      )
    `)
    .order('category_code', { ascending: true });

  if (error) {
    throw new Error(error.message);
  }

  return data as GradingPromptWithCategory[];
};

const fetchGradingPrompt = async (id: string): Promise<GradingPromptWithCategory> => {
  const { data, error } = await supabaseClient
    .from('grading_prompts')
    .select(`
      *,
      question_categories!grading_prompts_category_code_fkey (
        name,
        subject,
        paper_number,
        category
      )
    `)
    .eq('id', id)
    .single();

  if (error) {
    throw new Error(error.message);
  }

  return data as GradingPromptWithCategory;
};

const updateGradingPrompt = async (params: UpdateGradingPromptParams): Promise<GradingPromptRow> => {
  const { id, ...updateData } = params;

  const { data, error } = await supabaseClient
    .from('grading_prompts')
    .update({ ...updateData, updated_at: new Date().toISOString() })
    .eq('id', id)
    .select()
    .single();

  if (error) {
    throw new Error(error.message);
  }

  return data;
};

export const useAdminGradingPrompts = () => {
  return useQuery({
    queryKey: ['admin-grading-prompts'],
    queryFn: fetchGradingPrompts,
  });
};

export const useAdminGradingPrompt = (id: string | undefined) => {
  return useQuery({
    queryKey: ['admin-grading-prompt', id],
    queryFn: () => fetchGradingPrompt(id!),
    enabled: !!id,
  });
};

export const useUpdateGradingPrompt = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateGradingPrompt,
    onSuccess: (data) => {
      toast.success('Grading prompt updated successfully');
      queryClient.invalidateQueries({ queryKey: ['admin-grading-prompts'] });
      queryClient.invalidateQueries({ queryKey: ['admin-grading-prompt', data.id] });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to update grading prompt');
    },
  });
};

export default useAdminGradingPrompts;
